<script setup lang="ts">
import CardResult from '@/components/cards/CardResult.vue';
import ButtonSimple from '@/components/buttons/ButtonSimple.vue';
import { useFormulaResultStore } from '@/stores/formulaResultStore';
import { useFormulaFormStore } from '@/stores/formulaFormStore';
import { computed, ref } from 'vue';
import { monthName } from '@/helpers/dateHelper';
import { computeIcbInfo, getActiveAlerts } from '@/helpers/icbHelper';
import router from '@/router';
import { useRoute } from 'vue-router';
import Login from '@/components/layouts/Login.vue';
import { useAuthenticatedUserStore } from '@/stores/authenticatedUserStore';
import { EyeExamHttp } from '@/http/eyeExamHttp'
import { useMenssageStore } from '@/stores/menssage';
import NavbarFormula from "@/components/navbar/NavbarFormula.vue";
import {useI18n} from "vue-i18n";
import { jsPDF } from "jspdf";

const authenticatedUserStore = useAuthenticatedUserStore()
const formulaFormStore = useFormulaFormStore()
const formulaResultStore = useFormulaResultStore()
const messageStore = useMenssageStore()
const route = useRoute()

// Mesma trava do /formula — recurso em teste, só aparece com ?beta=torica
// na URL. Como o link pra essa página não carrega query string (fica só em
// memória via router.replace), o valor é lido uma vez e guardado.
const toricBetaEnabled = route.query.beta === 'torica'

const result = formulaResultStore.result
const loginWindowActive = ref(false)

if (formulaFormStore.data.type === undefined) {
  router.replace({ path: '/', query: route.query });
}

const i18n = useI18n();
const { t } = i18n;

const formatNum = (value: number) => {
  return value.toFixed(2).replace('.', ',')
}

// Repetido dentro de cada card (OD/OS), além do título geral da página, porque
// na prática o pessoal recorta só o resultado na hora de imprimir/fotografar
// e o título geral fica de fora — sem isso, não dá pra saber se foi a fórmula
// Padrão, Ceratotomia Radial, Miopia ou Hipermetropia.
const formulaLabel = computed(() => {
  return formulaFormStore.data.type ? t('result.title.' + formulaFormStore.data.type) : ''
})

const icbInfo = computed(() => {
  const od = formulaResultStore.result?.od?.[0]?.eyeParameters
  const os = formulaResultStore.result?.os?.[0]?.eyeParameters
  return computeIcbInfo(od, os, t)
})

const activeAlerts = computed(() => {
  return getActiveAlerts(icbInfo.value)
})

const dateTime = computed(() => {
    const month = monthName(result?.dateTime.getMonth() ?? 13, i18n.locale.value)
    const day = result?.dateTime.getDate()
    const year = result?.dateTime.getFullYear()
    const hour = result?.dateTime.getHours()
    const minutes = result?.dateTime.getMinutes()

    if (i18n.locale.value === 'pt-br')
      return `${day} de ${month} de ${year} às ${hour}:${minutes}`
    return `${month} ${day}, ${year} at ${hour}:${minutes}`
})

// Transforma um <svg> (o desenho do olho) numa imagem PNG de verdade, via
// Blob + <canvas> — evita os dois problemas que o html2canvas tem com SVG
// aninhado: sem foreignObjectRendering, ele nem tenta "carregar" o <svg>
// como imagem (erro "Error loading svg"); com foreignObjectRendering, ele
// carrega mas quebra em texto acentuado ("atob... not correctly encoded").
// Usando um Blob (em vez de data: URI) não precisa de base64/percent-encode
// nenhum, então nenhum dos dois bugs se aplica.
function svgToPngDataUrl(svgEl: SVGSVGElement): Promise<string> {
  return new Promise((resolve, reject) => {
    const svgString = new XMLSerializer().serializeToString(svgEl)
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)
    const img = new Image()
    const scale = 3 // resolução maior que o tamanho exibido, fica nítido no PDF

    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = 200 * scale
      canvas.height = 200 * scale
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        URL.revokeObjectURL(url)
        reject(new Error('Canvas 2D context indisponível'))
        return
      }
      ctx.scale(scale, scale)
      ctx.drawImage(img, 0, 0, 200, 200)
      URL.revokeObjectURL(url)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Falha ao carregar o SVG do desenho do olho'))
    }
    img.src = url
  })
}

// Troca cada <svg class="eye-axis-diagram"> do clone por um <img> apontando
// pra sua versão já rasterizada (PNG), mantendo o mesmo tamanho na página.
async function rasterizeEyeDiagrams(contentClone: HTMLElement): Promise<void> {
  const diagrams = Array.from(contentClone.querySelectorAll<SVGSVGElement>('svg.eye-axis-diagram'))

  await Promise.all(diagrams.map(async (svg) => {
    try {
      const pngDataUrl = await svgToPngDataUrl(svg)
      const img = document.createElement('img')
      img.src = pngDataUrl
      // Estilo direto no elemento, não classe: o CSS scoped do componente
      // original precisa de um atributo especial do Vue (data-v-xxxx) que
      // não copiamos pro <img> novo, então a classe sozinha não bate em
      // nada — sem isso a imagem sai no tamanho bruto do canvas (enorme).
      img.style.width = '100%'
      img.style.maxWidth = '170px'
      img.style.height = 'auto'
      img.style.display = 'block'
      img.style.margin = '0.6em auto 0'
      svg.replaceWith(img)
    } catch {
      // Se a conversão falhar por algum motivo, tira o desenho em vez de
      // arriscar quebrar a exportação inteira de novo.
      svg.remove()
    }
  }))
}

async function print(): Promise<void>
{
  const doc = new jsPDF('p', 'mm', 'a4');

  const content = document.getElementById('card-result-print');
  const contentClone: any = content?.cloneNode(true);

  await rasterizeEyeDiagrams(contentClone);

  const title = document.querySelector('.formula-title')?.cloneNode(true);

  if (title) {
    contentClone.insertBefore(title, contentClone.firstChild);
  }

  const header = document.querySelector('.header__identification')?.cloneNode(true);

  if (header) {
    contentClone.insertBefore(header, contentClone.firstChild);
  }

  const resultElement = contentClone.querySelector('.calculation-result__result');
  if (resultElement) {
    resultElement.style.paddingTop = '0';
    resultElement.style.display = 'flex';
    resultElement.style.flexDirection = 'row';
  }

  const parametersElements = contentClone.querySelectorAll('.parameters');
  if (parametersElements) {
    parametersElements.forEach((elem: any) => {
      elem.style.flexDirection = 'row';
      elem.style.gap = 'None';
    })
  }

  const parametersRowElements = contentClone.querySelectorAll('.parameters-row');
  if (parametersRowElements) {
    parametersRowElements.forEach((elem: any) => {
      elem.style.display = 'block';
      elem.style.gap = 'None';
      elem.style.baseline = 'None';
    })
  }

  const parametersRowSpanElements = contentClone.querySelectorAll('.parameters-row span');
  if (parametersRowSpanElements) {
    parametersRowSpanElements.forEach((elem: any) => {
      elem.style.width = '100%';
      elem.style.textAlign = 'left';
      elem.style.paddingLeft = '5px';
    })
  }

  const button = contentClone.querySelector("#calculation-result-buttons");
  if (button) {
    button.style.display = 'none';
  }

  doc.html(contentClone, {
    callback: function (doc) {
      doc.save('resultado-lucena.pdf');
    },
    x: 10,
    y: 10,
    width: 180,
    windowWidth: 1280,
    html2canvas: {
      scale: 0.15,
    },
  });

}

function newFormula(): void
{
    formulaFormStore.formulaForm.surgeon = undefined
    formulaFormStore.formulaForm.patient.name = undefined
    formulaFormStore.formulaForm.patient.year = 0
    formulaFormStore.formulaForm.od = undefined
    formulaFormStore.formulaForm.os = undefined
    formulaResultStore.isSaved = false
    router.push({ path: '/', query: route.query })
}

function save(): void
{
    if (! authenticatedUserStore.authenticatedUser) {
        loginWindowActive.value = true
        return
    }

    loginWindowActive.value = false

    if (! result) {
        return
    }

    EyeExamHttp.store(result).then(() => {
        formulaResultStore.isSaved = true
        messageStore.addSuccess(
            'Result saved successfully',
            'Access the management panel to add more information'
        )
    }).catch(() => {
        messageStore.addError(
            'Unable to save formula result',
            'Please inform us via email contato@ciebit.com'
        )
    })
}
</script>

<template>
    <Login 
        v-if="loginWindowActive"
        @cancelar="loginWindowActive = false"
        @authenticated="save"/>

    <div class="page--with-limit page--show">
      <NavbarFormula></NavbarFormula>
      <h1 class="formula-title"><b>{{formulaFormStore.data.type ? $t('result.title.' + formulaFormStore.data.type) : ''}}</b></h1>
      <hr />
        <div v-if="formulaResultStore.result" id="card-result-print" class="calculation-result">
            <div class="calculation-result__date">
                <p>{{ dateTime }}</p>
            </div>
            <div class="calculation-result__data-basic">
                <p class="text-topic-value">
                    <span class="text-topic-value__topic">{{$t('result.form.patient')}}:</span>
                    <span class="text-topic-value__value">{{ formulaResultStore.result.patient.name }}</span>
                </p>

                <p class="text-topic-value">
                    <span class="text-topic-value__topic">{{$t('result.form.surgeon')}}:</span>
                    <span class="text-topic-value__value">{{ formulaResultStore.result.surgeon }}</span>
                </p>
            </div>

            <div class="calculation-result__result">
                <div class="calculation-result__eye calculation-result__result-od">
                  <CardResult :title="$t('formula.form.od')" :text="$t('result.form.od_right')" :formula="formulaLabel" :calculatorResultData="formulaResultStore.result.od" eye="od" :show-torico="toricBetaEnabled" :formula-type="formulaFormStore.data.type"/>
                </div>

                <div class="calculation-result__eye calculation-result__result-os">
                  <CardResult :title="$t('formula.form.os')" :text="$t('result.form.os_left')" :formula="formulaLabel" :calculatorResultData="formulaResultStore.result.os" eye="os" :show-torico="toricBetaEnabled" :formula-type="formulaFormStore.data.type"/>
                </div>
            </div>

            <!-- ICB global reliability indicator and summary alerts -->
            <section class="icb-container" v-if="icbInfo && activeAlerts.length > 0">
              <div class="alerts-summary">
                <h3>{{ $t('icb.summary_title') }}</h3>
                <ul class="alerts-summary__list">
                  <li v-for="alert in activeAlerts" :key="alert.key" class="alerts-summary__item" :class="'alerts-summary__item--' + alert.status">
                    <div class="alerts-summary__content">
                      <div class="alerts-summary__title-line">
                        <span class="icb-dot" :class="'icb-dot--' + alert.status"></span>
                        <strong>{{ alert.label }}</strong>
                      </div>
                      <div class="alerts-summary__medidas-line">
                        Medidas: OD={{ formatNum(alert.od) }} {{ alert.unit }} / OE= {{ formatNum(alert.os) }} {{ alert.unit }} (dif: {{ formatNum(alert.diff) }} {{ alert.unit }})
                      </div>
                      <div class="alerts-summary__msg-line">
                        {{ alert.message }}
                      </div>
                      <div class="alerts-summary__limits-line">
                        Limites: 
                        <span class="icb-dot icb-dot--green"></span> &lt; {{ formatNum(alert.limits.green) }} {{ alert.unit }} 
                        <span class="icb-dot icb-dot--yellow"></span> &gt;= {{ formatNum(alert.limits.yellowMin) }} {{ alert.unit }} e &lt;= {{ formatNum(alert.limits.yellowMax) }} {{ alert.unit }} 
                        <span class="icb-dot icb-dot--red"></span> &gt; {{ formatNum(alert.limits.red) }} {{ alert.unit }}
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </section>

            <div class="calculation-result__buttons" id="calculation-result-buttons">
                <ButtonSimple @click="print">{{ $t('result.actions.print') }}</ButtonSimple>
                <ButtonSimple @click="newFormula">{{ $t('result.actions.new') }}</ButtonSimple>
                <ButtonSimple @click="$router.push({ path: '/formula', query: route.query })">{{ $t('result.actions.edit') }}</ButtonSimple>
                <ButtonSimple @click="save" v-if="! formulaResultStore.isSaved">{{ $t('result.actions.save') }}</ButtonSimple>
            </div>
        </div>
        <p v-else>No result</p>
    </div>
</template>

<style scoped lang="sass">
@use "../assets/colors/colors"
@use "../assets/fonts/fonts"

.page--with-limit
  position: relative
.calculation-result
  &__data-basic
    display: flex
    font-size: 2em
    flex-direction: column
    gap: 1em

    @media print
      padding-top: 2em

    @media print, (min-width: 860px)
      flex-direction: row
      gap: 3em

    & .text-topic-value__topic
      @include fonts.medium
      padding-right: 0.5em

  &__date
    font-size: 1.4em
    color: colors.$secondary
    padding-bottom: 3.4em

    @media print
        padding-bottom: 0

  &__result
    display: flex
    flex-direction: column
    gap: 3em

    @media screen
      padding-top: 5.7em

    @media print
      padding-top: 2em

    @media print, (min-width: 860px)
      flex-direction: row
      gap: 3.68%

    &-od
      @media screen
        color: colors.$tertiary

    &-os
      @media screen
        color: colors.$quaternary

  &__eye
    flex-grow: 1
    width: 100%

  &__buttons
    flex-wrap: wrap
    gap: 2.40%
    padding-top: 7.06%
    justify-content: center
    margin-bottom: 100px

    @media screen
      display: flex

    @media print
      display: none

.formula-title
  font-size: 2em

  @media print
    margin-top: 20px

.icb-container
  margin-top: 2rem
  margin-bottom: 0.5rem
  display: flex
  flex-direction: column
  gap: 2rem

.alerts-summary
  display: flex
  flex-direction: column
  gap: 1.2rem
  text-align: left

  h3
    font-size: 1.6rem
    font-weight: 600
    color: colors.$secondary
    border-bottom: 1px solid colors.$primary-and-secundary-contrast
    padding-bottom: 0.5rem

  &__list
    list-style: none
    padding: 0
    margin: 0
    display: flex
    flex-direction: column
    gap: 1rem

  &__item
    display: flex
    align-items: flex-start
    gap: 1rem
    padding: 1.2rem
    border-radius: 6px
    background-color: colors.$secondary-contrast
    border: 1px solid colors.$border
    -webkit-print-color-adjust: exact
    print-color-adjust: exact

    &--green
      border-left: 4px solid #22c55e
    &--yellow
      border-left: 4px solid #eab308
    &--red
      border-left: 4px solid #ef4444

  &__content
    display: flex
    flex-direction: column
    gap: 0.4rem
    font-size: 1.3rem
    color: colors.$secondary

    strong
      font-size: 1.4rem
      color: colors.$secondary

  &__title-line
    display: flex
    align-items: center
    gap: 0.5rem

.icb-dot
  width: 12px
  height: 12px
  border-radius: 50%
  display: inline-block
  margin: 0 4px
  vertical-align: middle
  flex-shrink: 0
  -webkit-print-color-adjust: exact
  print-color-adjust: exact

  &--green
    background-color: #22c55e
  &--yellow
    background-color: #eab308
  &--red
    background-color: #ef4444

</style>